/**
 * Classify routes for campaign attribution (Milestone 27).
 */

/** Known SEO city slugs — keep in sync with seoPageConstants SEO_CITIES. */
const SEO_CITY_SLUG_PATTERN =
  "medellin|envigado|sabaneta|itagui|bello|rionegro|bogota|cali";

/** Maps registry `pageType` string values → attribution categories (no constants import at init). */
const SEO_TYPE_MAP = {
  city: "seo_city",
  city_body_type: "seo_body_type",
  city_financing: "seo_financing",
  city_transmission: "seo_transmission",
  city_economy: "seo_budget",
  make: "seo_make",
  make_city: "seo_make",
  model: "seo_model",
  model_city: "seo_model",
  body_type: "seo_body_type",
  body_type_city: "seo_body_type",
  budget: "seo_budget",
  budget_city: "seo_budget",
  dealer_city: "dealer_directory",
  guide: "guide",
};

const SEO_BODY_TYPE_PATHS = {
  "/camionetas-usadas-medellin": "camionetas-medellin",
  "/automoviles-usados-medellin": "automoviles-medellin",
  "/pickups-usadas-medellin": "pickups-medellin",
  "/carros-familiares-medellin": "familiares-medellin",
};

function normalizeAttributionPath(path) {
  if (!path) return "/";
  const trimmed = String(path).split("?")[0].replace(/\/+$/, "") || "/";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function buildSeoLandingMatch(pageType, slug) {
  return {
    pageType: SEO_TYPE_MAP[pageType] || "unknown",
    seoPageType: pageType,
    seoPageSlug: slug,
    landingPageType: SEO_TYPE_MAP[pageType] || pageType,
  };
}

function matchSeoLanding(pathname) {
  const path = normalizeAttributionPath(pathname);

  const cityMatch = path.match(new RegExp(`^/carros-usados-(${SEO_CITY_SLUG_PATTERN})$`));
  if (cityMatch) return buildSeoLandingMatch("city", cityMatch[1]);

  if (SEO_BODY_TYPE_PATHS[path]) {
    return buildSeoLandingMatch("city_body_type", SEO_BODY_TYPE_PATHS[path]);
  }

  const financingMatch = path.match(new RegExp(`^/carros-financiados-(${SEO_CITY_SLUG_PATTERN})$`));
  if (financingMatch) return buildSeoLandingMatch("city_financing", `financiados-${financingMatch[1]}`);

  const transmissionMatch = path.match(new RegExp(`^/carros-automaticos-(${SEO_CITY_SLUG_PATTERN})$`));
  if (transmissionMatch) {
    return buildSeoLandingMatch("city_transmission", `automaticos-${transmissionMatch[1]}`);
  }

  const economyMatch = path.match(new RegExp(`^/carros-economicos-(${SEO_CITY_SLUG_PATTERN})$`));
  if (economyMatch) return buildSeoLandingMatch("city_economy", `economicos-${economyMatch[1]}`);

  const budgetMatch = path.match(/^\/presupuesto\/([a-z0-9-]+)\/([a-z0-9-]+)$/);
  if (budgetMatch) return buildSeoLandingMatch("budget_city", `${budgetMatch[1]}-${budgetMatch[2]}`);

  const makeMatch = path.match(/^\/marcas\/([a-z0-9-]+)\/([a-z0-9-]+)$/);
  if (makeMatch) return buildSeoLandingMatch("make_city", `${makeMatch[1]}-${makeMatch[2]}`);

  const modelMatch = path.match(/^\/modelos\/([a-z0-9-]+)\/([a-z0-9-]+)\/([a-z0-9-]+)$/);
  if (modelMatch) {
    return buildSeoLandingMatch("model_city", `${modelMatch[1]}-${modelMatch[2]}-${modelMatch[3]}`);
  }

  const dealerCityMatch = path.match(new RegExp(`^/concesionarios/(${SEO_CITY_SLUG_PATTERN})$`));
  if (dealerCityMatch) return buildSeoLandingMatch("dealer_city", dealerCityMatch[1]);

  return null;
}

export function getPageAttributionContext(pathname = "", routeParams = {}) {
  const path = pathname || "";
  const seo = matchSeoLanding(path);

  if (seo) {
    return {
      pageType: seo.pageType,
      pagePath: path,
      landingPageType: seo.landingPageType,
      landingPagePath: path,
      seoPageType: seo.seoPageType,
      seoPageSlug: seo.seoPageSlug,
      guideSlug: seo.pageType === "guide" ? seo.seoPageSlug : null,
      listingId: null,
      dealerId: null,
    };
  }

  // TODO(Lead Flow M2): add explicit `/concesionarios` → dealer_directory (currently falls through to unknown).
  if (path === "/comprar/carros-usados") {
    return { pageType: "search_results", pagePath: path, landingPagePath: path, landingPageType: "search_results" };
  }
  if (path === "/concesionarios" || path === "/v/automap/concesionarios") {
    return { pageType: "dealer_directory", pagePath: path, landingPagePath: path, landingPageType: "dealer_directory" };
  }
  if (path.startsWith("/carros/")) {
    const listingId = path.replace(/^\/carros\//, "").split("/")[0] || routeParams.vehicleListingId || null;
    return { pageType: "vehicle_detail", pagePath: path, listingId, landingPagePath: path, landingPageType: "vehicle_detail" };
  }
  if (path.startsWith("/concesionarios/")) {
    const dealerSlug = path.replace(/^\/concesionarios\//, "").split("/")[0] || routeParams.dealerSlug || null;
    return {
      pageType: "dealer_profile",
      pagePath: path,
      dealerId: dealerSlug,
      landingPagePath: path,
      landingPageType: "dealer_profile",
    };
  }
  if (path === "/checklist-compra" || path === "/mi-checklist") {
    return { pageType: "checklist", pagePath: path, landingPagePath: path, landingPageType: "checklist" };
  }
  if (path === "/vender-mi-carro") {
    return { pageType: "sell_your_car", pagePath: path, landingPagePath: path, landingPageType: "sell_your_car" };
  }
  if (path === "/servicios" || path.startsWith("/servicios/")) {
    return { pageType: "services", pagePath: path, landingPagePath: path, landingPageType: "services" };
  }
  if (path.startsWith("/guias/")) {
    const guideSlug = path.replace(/^\/guias\//, "").split("/")[0];
    return { pageType: "guide", pagePath: path, guideSlug, landingPagePath: path, landingPageType: "guide" };
  }
  if (path === "/seguridad" || path.startsWith("/guias/compra-segura")) {
    return { pageType: "safety", pagePath: path, landingPagePath: path, landingPageType: "safety" };
  }

  if (/\/v\/[^/]+\/comprar\/quiz\/resultados/.test(path)) {
    return { pageType: "recommendation_results", pagePath: path, landingPagePath: path, landingPageType: "recommendation_results" };
  }
  if (/\/v\/[^/]+\/comprar\/quiz/.test(path)) {
    return { pageType: "buyer_quiz", pagePath: path, landingPagePath: path, landingPageType: "buyer_quiz" };
  }
  if (/\/v\/[^/]+\/vehicles\/[^/]+/.test(path)) {
    const listingId = path.split("/vehicles/")[1]?.split("/")[0] || routeParams.vehicleListingId || null;
    return { pageType: "vehicle_detail", pagePath: path, listingId, landingPagePath: path, landingPageType: "vehicle_detail" };
  }
  if (/\/v\/[^/]+\/vehicles/.test(path)) {
    return { pageType: "search_results", pagePath: path, landingPagePath: path, landingPageType: "search_results" };
  }
  if (/\/v\/[^/]+\/concesionarios\/[^/]+/.test(path)) {
    const dealerSlug = path.split("/concesionarios/")[1]?.split("/")[0] || routeParams.dealerSlug || null;
    return { pageType: "dealer_profile", pagePath: path, dealerId: dealerSlug, landingPagePath: path, landingPageType: "dealer_profile" };
  }
  if (/\/v\/[^/]+\/concesionarios\/?$/.test(path)) {
    return { pageType: "dealer_directory", pagePath: path, landingPagePath: path, landingPageType: "dealer_directory" };
  }
  if (/\/v\/[^/]+\/?$/.test(path)) {
    return { pageType: "home", pagePath: path, landingPagePath: path, landingPageType: "home" };
  }

  return { pageType: "unknown", pagePath: path, landingPagePath: path, landingPageType: "unknown" };
}
